import { useEffect, useState } from 'react';
import type { App } from '@hangar/shared';
import { toast } from 'sonner';
import { useEnvRequirements, useEnvVarMutations } from '@/api/hooks';
import type { EnvRequirement } from '@/api/schemas';
import { hasDuplicateKey, hasInvalidKey, newRequirement, removeRow, rowsToSave, savedDescription, updateRow } from './requirementsModel';

/** The rows being edited: reset from the server data whenever it arrives, edited locally. */
function useRequirementRows(data: { requirements: EnvRequirement[] } | undefined) {
  const [rows, setRows] = useState<EnvRequirement[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (data) {
      setRows(data.requirements);
      setDirty(false);
    }
  }, [data]);
  const edit = (change: (r: EnvRequirement[]) => EnvRequirement[]) => {
    setRows(change);
    setDirty(true);
  };
  return {
    rows,
    dirty,
    clean: () => setDirty(false),
    update: (i: number, patch: Partial<EnvRequirement>) => edit((r) => updateRow(r, i, patch)),
    remove: (i: number) => edit((r) => removeRow(r, i)),
    add: () => edit((r) => [...r, newRequirement()]),
  };
}

/** Rows being edited for one app: loaded from the API, edited locally, saved whole (`PUT` with the ETag). */
export function useRequirementsEditor(apps: App[] | undefined, initialAppSlug: string | undefined) {
  const [appSlug, setAppSlug] = useState(initialAppSlug ?? apps?.[0]?.slug ?? '');
  const app = apps?.find((a) => a.slug === appSlug);
  const query = useEnvRequirements(app?.id);
  const { requirements: save } = useEnvVarMutations();
  const edited = useRequirementRows(query.data);
  const [submitted, setSubmitted] = useState(false);

  const onSave = async () => {
    setSubmitted(true);
    if (!app || hasInvalidKey(edited.rows) || hasDuplicateKey(edited.rows)) return;
    try {
      const result = await save.mutateAsync({ appId: app.id, requirements: rowsToSave(edited.rows), etag: query.data?.etag });
      toast.success(`Requirements saved`, { description: savedDescription(result.requirements.length, app.slug) });
      edited.clean();
      setSubmitted(false);
    } catch {
      /* ProblemAlert */
    }
  };
  return { appSlug, setAppSlug, app, query, save, submitted, onSave, ...edited };
}

export type RequirementsEditorApi = ReturnType<typeof useRequirementsEditor>;
